import * as Yup from 'yup';

import { ALLOWANCE, CHARGE } from '../constants/defaults';
import { isOutOfScopeDocumentType } from '../utils/documentTypeRules';
import { isMarginScheme } from '../utils/helper';
import { createAllowanceChargeSchema } from './allowanceChargeValidation';
import { standardRateAdditionalTaxRatePositiveTest } from './taxCategoryValidation';

const getEffectiveDocumentTypeCode = (ctx) => {
  const contextType = ctx?.options?.context?.invoice_type_code ?? ctx?.options?.context?.credit_note_type_code ?? null;
  const rootValue =
    ctx?.from && Array.isArray(ctx.from) && ctx.from.length > 0 ? ctx.from[ctx.from.length - 1]?.value : null;
  const rootType = rootValue?.invoice_type_code ?? rootValue?.credit_note_type_code ?? null;

  return contextType ?? rootType;
};

const getEffectiveProfileExecutionId = (ctx) => {
  const contextProfileId = ctx?.options?.context?.profile_execution_id ?? null;
  const rootValue =
    ctx?.from && Array.isArray(ctx.from) && ctx.from.length > 0 ? ctx.from[ctx.from.length - 1]?.value : null;

  return contextProfileId ?? rootValue?.profile_execution_id ?? null;
};

const getAllowanceChargeMessages = (messages = {}) => messages?.allowance_charge || {};

export const getLineItemValidationSchema = (messages = {}) => {
  const m = messages?.line_item || {};
  const allowanceChargeMessages = getAllowanceChargeMessages(messages);

  const allowanceValidationSchema = createAllowanceChargeSchema(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
    ALLOWANCE,
    false,
    false,
    false,
    null,
    allowanceChargeMessages,
  );

  const chargeValidationSchema = createAllowanceChargeSchema(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
    CHARGE,
    false,
    false,
    false,
    null,
    allowanceChargeMessages,
  );

  return Yup.object().shape({
    name: Yup.string().min(1, m.name_min_length).max(100, m.name_max_length).required(m.item_name_required),
    description: Yup.string().required(m.description_required),
    unit_code: Yup.mixed().nullable().required(m.unit_code_required),
    commodity_code: Yup.mixed().nullable(),

    item_classification_identifier: Yup.mixed().when('commodity_code', {
      is: (val) => val === 'G' || val === 'B',
      then: (schema) => schema.required('Item classification code is required'),
      otherwise: (schema) => schema.nullable(),
    }),

    service_accounting_code: Yup.mixed().when('commodity_code', {
      is: (val) => val === 'S' || val === 'B',
      then: (schema) => schema.required('Service accounting code is required'),
      otherwise: (schema) => schema.nullable(),
    }),
    quantity: Yup.number()
      .transform((value, originalValue) => {
        return !originalValue ? undefined : value;
      })
      .required(m.quantity_required)
      .min(1, m.quantity_min_one),
    price: Yup.object().shape({
      base_amount: Yup.number()
        .transform((value, originalValue) => {
          if (originalValue == null || originalValue === '') {
            return undefined;
          }
          return value;
        })
        .required(m.base_amount_required)
        .min(0, m.base_amount_min_zero),
      base_quantity: Yup.number(),
    }),
    classified_tax_category: Yup.object().shape({
      id: Yup.string()
        .nullable()
        .required(m.tax_category_required)
        .test('out-of-scope-tax-categories-only', m.out_of_scope_allowed_tax_categories, function (value) {
          if (!isOutOfScopeDocumentType(getEffectiveDocumentTypeCode(this))) {
            return true;
          }

          return ['E', 'O', 'Z'].includes(value || '');
        })
        .test('margin-scheme-tax-categories-only', m.margin_scheme_allowed_tax_categories, function (value) {
          if (!isMarginScheme(getEffectiveProfileExecutionId(this))) {
            return true;
          }

          return value === 'N';
        }),
      percent: Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .when('id', {
          is: (id) => id === 'E' || id === 'O',
          then: (schema) => schema.notRequired().default(0),
          otherwise: (schema) => schema.required(m.tax_rate_required),
        })
        .min(0, m.tax_rate_min_zero)
        .max(100, m.tax_rate_max_hundred)
        .test('out-of-scope-tax-rate-zero', m.out_of_scope_tax_rate_must_be_zero, function (value) {
          if (!isOutOfScopeDocumentType(getEffectiveDocumentTypeCode(this))) {
            return true;
          }

          return Number(value) === 0;
        })
        .test(
          'standard-rate-additional-tax-rate-positive',
          m.standard_rate_additional_tax_rate_must_be_positive,
          standardRateAdditionalTaxRatePositiveTest,
        ),
      tax_exemption_reason_code: Yup.string()
        .nullable()
        .test('tax-exemption-reason-required', m.tax_exemption_reason_required_for_e, function (value) {
          const { id } = this.parent;
          if (id === 'E') {
            return value !== null && value !== undefined && value !== '';
          }
          return true;
        }),
      tax_exemption_reason: Yup.string().nullable(),
      tax_scheme: Yup.string().required(m.tax_scheme_required || 'Tax scheme is required'),
    }),
    nature_code: Yup.string()
      .nullable()
      .test('nature-code-required', m.goods_or_service_type_required_for_ae, function (value) {
        const { classified_tax_category } = this.parent;
        if (classified_tax_category?.id === 'AE') {
          return value !== null && value !== undefined && value !== '';
        }
        return true;
      }),
    allowances: Yup.array().of(allowanceValidationSchema),
    charges: Yup.array().of(chargeValidationSchema),
  });
};

export const getProductValidationSchema = (messages = {}) => {
  const m = messages?.line_item || {};

  return Yup.object().shape({
    name: Yup.string().min(1, m.name_min_length).max(100, m.name_max_length).required(m.item_name_required),
    quantity: Yup.number().min(1, m.quantity_min_one),
    description: Yup.string().min(0).max(300, m.description_max_length).nullable(),
    unit_code: Yup.mixed().nullable(),
    commodity_code: Yup.mixed().nullable(),
    item_classification_identifier: Yup.mixed().when('commodity_code', {
      is: (val) => {
        const code = typeof val === 'object' ? val?.value : val;
        return code === 'G' || code === 'B';
      },
      then: (schema) => schema.required(m.item_classification_required || 'HSN code is required for goods'),
      otherwise: (schema) => schema.nullable(),
    }),
    service_accounting_code: Yup.mixed().when('commodity_code', {
      is: (val) => {
        const code = typeof val === 'object' ? val?.value : val;
        return code === 'S' || code === 'B';
      },
      then: (schema) => schema.required(m.service_accounting_required || 'SAC code is required for services'),
      otherwise: (schema) => schema.nullable(),
    }),
    unit_price: Yup.number()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null || originalValue === undefined) {
          return undefined;
        }

        return value;
      })
      .required(m.unit_price_required ?? m.base_amount_required)
      .min(0, m.unit_price_min_zero ?? m.base_amount_min_zero),
    classified_tax_category: Yup.object().shape({
      id: Yup.string().nullable(),
      percent: Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .when('id', {
          is: (id) => id === 'E' || id === 'O',
          then: (schema) => schema.notRequired().default(0),
          otherwise: (schema) => schema.required(m.tax_rate_required),
        })
        .min(0, m.tax_rate_min_zero)
        .max(100, m.tax_rate_max_hundred),
      tax_exemption_reason_code: Yup.string().when('id', {
        is: (id) => id === 'E',
        then: (schema) => schema.required(m.tax_exemption_reason_required_for_exempt),
        otherwise: (schema) => schema.nullable(),
      }),
      tax_exemption_reason: Yup.string().nullable(),
    }),
    nature_code: Yup.string()
      .nullable()
      .test('nature-code-required', m.goods_or_service_type_required_for_ae, function (value) {
        const { classified_tax_category } = this.parent;
        if (classified_tax_category?.id === 'AE') {
          return value !== null && value !== undefined && value !== '';
        }
        return true;
      }),
  });
};

export const getNotesValidationSchema = (messages = {}) => {
  const m = messages?.line_item || {};

  return Yup.object().shape({
    notes: Yup.string().max(1000, m.notes_max_length),
  });
};

export const lineItemValidationSchema = getLineItemValidationSchema({});

export const productVaidationSchema = getProductValidationSchema({});

export const NotesValidationSchema = getNotesValidationSchema({});
